/**
 * MarkupEditor: Main editor component with drawing canvas and tools
 * Feature: 049-critique-photo-markup
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';
import type { AdaptivePhotoProps } from '@/components/photos/AdaptivePhoto';
import { MarkupCanvas } from './MarkupCanvas';
import { ShapeToolbar } from './ShapeToolbar';
import { getToolMetadata } from '@/utils/markupToolRegistry';
import type { MarkupShape, ShapeType, ShapeStyle } from '@/types/markup.types';
import { MARKUP_CONSTRAINTS } from '@/types/markup.types';
import { renumberShapes } from '@/utils/markupValidation';
import { hitTestShapes } from '@/utils/markupHitTest';
import {
  loadMarkupStylePreferences,
  saveMarkupStylePreferences,
  loadSelectedTool,
  saveSelectedTool,
} from '@/utils/markupStylePersistence';
import logger from '@/utils/logger';
import { cn } from '@/lib/utils';

// Import central tool registration (registers all tools)
import '@/utils/markupTools';

interface MarkupEditorProps {
  photo: AdaptivePhotoProps['photo'];
  shapes: MarkupShape[];
  className?: string;
  onShapesChange: (shapes: MarkupShape[]) => void;
  onValidationError: (message: string) => void;
}

export function MarkupEditor({
  photo,
  shapes,
  className,
  onShapesChange,
  onValidationError,
}: MarkupEditorProps) {
  // Load saved tool and style preferences from localStorage
  const [selectedTool, setSelectedTool] = useState<ShapeType | null>(() => loadSelectedTool());
  // Use shape.number for selection (IDs are optional and not stored)
  const [selectedShapeId, setSelectedShapeId] = useState<number | null>(null);
  const [currentStyle, setCurrentStyle] = useState<ShapeStyle>(() => loadMarkupStylePreferences());
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate photo aspect ratio from variants (use first available variant with dimensions)
  const photoAspectRatio = (() => {
    const variant = photo.variants.find((v) => v.width && v.height);
    if (variant && variant.width && variant.height) {
      return variant.width / variant.height;
    }
    return 1; // Default to square if no dimensions available
  })();

  /**
   * Persist selected tool to localStorage whenever it changes
   */
  useEffect(() => {
    saveSelectedTool(selectedTool);
  }, [selectedTool]);

  /**
   * Handle validation errors (deselect current shape, log error)
   */
  const handleValidationError = useCallback((message: string) => {
    logger.warn('MarkupEditor', 'Validation error:', message);
    // Deselect current shape if one is selected
    if (selectedShapeId) {
      setSelectedShapeId(null);
    }
    // Pass through to parent for special cases (like max shapes)
    if (message !== 'click') {
      onValidationError(message);
    }
  }, [selectedShapeId, onValidationError]);

  /**
   * Add new shape with auto-assigned number
   * Tool remains selected for continuous drawing
   */
  const handleShapeComplete = useCallback(
    (newShape: MarkupShape) => {
      // Check max shapes limit
      if (shapes.length >= MARKUP_CONSTRAINTS.MAX_SHAPES) {
        logger.warn('MarkupEditor', 'Max shapes limit reached');
        onValidationError(`Maximum ${MARKUP_CONSTRAINTS.MAX_SHAPES} shapes allowed`);
        return;
      }

      // Assign sequential number
      const nextNumber = shapes.length + 1;
      const numberedShape = { ...newShape, number: nextNumber };

      logger.info('MarkupEditor', 'Adding shape:', numberedShape);
      onShapesChange([...shapes, numberedShape]);

      // Tool remains selected for continuous drawing (sticky tool behavior)
    },
    [shapes, onShapesChange, onValidationError]
  );

  /**
   * Handle shape click for selection
   */
  const handleShapeClick = useCallback((shapeNumber: number) => {
    logger.debug('MarkupEditor', 'Shape selected:', shapeNumber);
    setSelectedShapeId(shapeNumber);
  }, []);

  /**
   * Check if a click (in percentage coords) hits an existing shape
   * If yes, select it and return the shape number; if no, return null to allow drawing
   */
  const handleToolClick = useCallback(
    (x: number, y: number): number | null => {
      const viewBoxHeight = 100 / photoAspectRatio;
      const hitShapeNumber = hitTestShapes(x, y, shapes, viewBoxHeight);
      if (hitShapeNumber !== null) {
        logger.debug('MarkupEditor', 'Tool click hit existing shape:', hitShapeNumber);
        setSelectedShapeId(hitShapeNumber);
        return hitShapeNumber;
      }
      return null; // Allow tool to start drawing
    },
    [shapes, photoAspectRatio]
  );

  /**
   * Handle background click to deselect
   */
  const handleBackgroundClick = useCallback(() => {
    logger.debug('MarkupEditor', 'Deselected shape');
    setSelectedShapeId(null);
  }, []);

  /**
   * Handle shape movement (drag)
   */
  const handleShapeMove = useCallback(
    (shapeNumber: number, movedShape: MarkupShape) => {
      const newShapes = shapes.map((s) => (s.number === shapeNumber ? movedShape : s));
      onShapesChange(newShapes);
    },
    [shapes, onShapesChange]
  );

  /**
   * Handle style changes - update selected shape or set default for new shapes
   * Persists style preferences to localStorage
   */
  const handleStyleChange = useCallback(
    (newStyle: ShapeStyle) => {
      setCurrentStyle(newStyle);

      // Persist style preferences to localStorage
      saveMarkupStylePreferences(newStyle);

      // If a shape is selected, update its style in real-time
      if (selectedShapeId !== null) {
        const newShapes = shapes.map((s) =>
          s.number === selectedShapeId
            ? {
                ...s,
                strokeWidth: newStyle.strokeWidth,
                strokeColor: newStyle.strokeColor,
                strokeDasharray: newStyle.strokeDasharray,
              }
            : s
        );

        logger.info('MarkupEditor', 'Updated shape style:', selectedShapeId);
        onShapesChange(newShapes);
      }
    },
    [selectedShapeId, shapes, onShapesChange]
  );

  /**
   * When a shape is selected, update the current style to match it
   */
  useEffect(() => {
    if (selectedShapeId !== null) {
      const selectedShape = shapes.find((s) => s.number === selectedShapeId);
      if (selectedShape) {
        setCurrentStyle({
          strokeWidth: selectedShape.strokeWidth,
          strokeColor: selectedShape.strokeColor,
          strokeDasharray: selectedShape.strokeDasharray,
        });
      }
    }
  }, [selectedShapeId, shapes]);

  /**
   * Handle keyboard events for shape deletion and renumbering
   * Uses useCallback to ensure stable event listener reference
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (selectedShapeId === null) return;

      // Delete selected shape (Delete or Backspace)
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        logger.info('MarkupEditor', 'Deleting shape:', selectedShapeId);

        // Remove shape and renumber remaining shapes
        const newShapes = shapes
          .filter((s) => s.number !== selectedShapeId)
          .map((s, index) => ({ ...s, number: index + 1 }));

        onShapesChange(newShapes);
        setSelectedShapeId(null);
        return;
      }

      // Renumber selected shape (number keys 1-9)
      const num = parseInt(e.key, 10);
      if (num >= MARKUP_CONSTRAINTS.MIN_NUMBER && num <= MARKUP_CONSTRAINTS.MAX_NUMBER) {
        // Only renumber if the number is valid for current shape count
        if (num <= shapes.length) {
          e.preventDefault();
          logger.info('MarkupEditor', 'Renumbering shape:', selectedShapeId, 'to', num);

          // Find the shape by number to get its id for renumberShapes
          const selectedShape = shapes.find((s) => s.number === selectedShapeId);
          if (selectedShape) {
            const newShapes = renumberShapes(shapes, selectedShapeId, num);
            onShapesChange(newShapes);
          }
        }
      }
    },
    [selectedShapeId, shapes, onShapesChange]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Toolbar with style controls */}
      <ShapeToolbar
        selectedTool={selectedTool}
        onToolSelect={setSelectedTool}
        currentStyle={currentStyle}
        onStyleChange={handleStyleChange}
        disabled={shapes.length >= MARKUP_CONSTRAINTS.MAX_SHAPES}
      />

      {/* Canvas container */}
      <div
        className="flex-1 bg-surface-2 overflow-hidden"
        ref={containerRef}
        role="application"
        aria-label="Markup annotation editor"
      >
        {/* Photo with markup overlay */}
        <div className="relative w-full h-full min-h-0">
          <AdaptivePhoto
            photo={photo}
            className="w-full"
            imgClassName="w-full max-h-[70vh] h-auto object-contain"
            alt={`Photo being annotated${photo.description ? `: ${photo.description}` : ''}`}
            preferStorageUrl={true}
          />

          {/* Markup canvas overlay */}
          <div
            className="absolute top-0 left-0 w-full h-full"
            role="img"
            aria-label={`Markup overlay with ${shapes.length} annotation${shapes.length !== 1 ? 's' : ''}`}
          >
            <MarkupCanvas
              shapes={shapes}
              selectedShapeId={selectedShapeId}
              onShapeClick={handleShapeClick}
              onBackgroundClick={handleBackgroundClick}
              onShapeMove={handleShapeMove}
              photoAspectRatio={photoAspectRatio}
            />
          </div>

          {/* Active drawing tool (dynamically rendered from registry) */}
          {selectedTool && (() => {
            const toolMetadata = getToolMetadata(selectedTool);
            if (!toolMetadata) return null;

            const ToolComponent = toolMetadata.Component;
            return (
              <ToolComponent
                currentStyle={currentStyle}
                onShapeComplete={handleShapeComplete}
                onValidationError={handleValidationError}
                onToolClick={handleToolClick}
                onShapeClick={handleShapeClick}
                selectedShapeId={selectedShapeId}
                shapes={shapes}
                onShapeMove={handleShapeMove}
                containerRef={containerRef}
                photoAspectRatio={photoAspectRatio}
              />
            );
          })()}
        </div>
      </div>

      {/* Shape count indicator */}
      <div className="p-2 bg-surface-1 border-t border-border text-sm text-muted">
        Shapes: {shapes.length} / {MARKUP_CONSTRAINTS.MAX_SHAPES}
        {selectedShapeId && <span className="ml-4 text-primary">Shape selected - use style controls or press Delete</span>}
      </div>
    </div>
  );
}
